/**
 * Client HTTP unique du back-office.
 *
 * **Un seul point de sortie vers le backend**, comme côté Flutter. Toute
 * requête porte un identifiant de corrélation (`AR-18`), respecte un délai
 * maximal, et rend une erreur **typée** dont le message se lit dans les
 * fichiers de traduction — jamais une chaîne technique brute affichée à un
 * analyste.
 *
 * **Aucun appel métier ici.** Les epics 1 à 14 poseront leurs points d'accès ;
 * ce fichier ne porte que le transport.
 *
 * **Ce que le web ne peut pas faire.** L'épinglage de certificat, posé côté
 * mobile, n'a pas d'équivalent dans un navigateur : la chaîne de confiance
 * appartient au navigateur et à son magasin de racines. Le point d'extension
 * équivalent est [ApiClientOptions.prepareRequest], où la story de durcissement
 * posera l'en-tête d'authentification et le jeton anti-rejeu. C'est écrit ici
 * pour que l'absence ne passe pas pour un oubli.
 */

/**
 * La nature d'un échec, indépendante du texte affiché.
 *
 * **`unauthorized` et `forbidden` sont deux choses, et les confondre coûtait cher.**
 * `unauthorized` (401) dit « votre session est finie » : elle se répare par un
 * renouvellement, et [createRenewingApiClient] le tente. `forbidden` (403) dit « votre
 * rôle n'a pas ce droit » : aucune reconnexion ne l'obtiendra, et réessayer ne fait que
 * refaire tourner le couple de jetons de l'opérateur à chaque écran qu'il n'a pas le
 * droit d'ouvrir. Tant que les deux partageaient une seule nature, le back-office
 * rejouait chaque refus d'habilitation — une rotation de session, puis un second appel
 * refusé pareillement.
 */
export type ApiErrorKind = 'network' | 'timeout' | 'unauthorized' | 'forbidden' | 'server';

/** Un échec d'appel, portant la clé de traduction de son message. */
export class ApiError extends Error {
  /** Ce qui a échoué. */
  readonly kind: ApiErrorKind;
  /** La clé de traduction du message destiné à l'utilisateur. */
  readonly messageKey: string;
  /** Le code HTTP, quand il y en a un. */
  readonly status: number | null;
  /** L'identifiant de corrélation de la requête, à citer dans un signalement. */
  readonly correlationId: string;

  constructor(options: {
    kind: ApiErrorKind;
    status: number | null;
    correlationId: string;
  }) {
    super(
      `Appel refusé (${options.kind}${options.status === null ? '' : ` ${options.status}`}), ` +
        `corrélation ${options.correlationId}`,
    );
    this.name = 'ApiError';
    this.kind = options.kind;
    this.messageKey = `errors.${options.kind}`;
    this.status = options.status;
    this.correlationId = options.correlationId;
  }
}

/** L'en-tête qui transporte l'identifiant de corrélation (`AR-18`). */
export const correlationHeader = 'X-Correlation-Id';

/** Ce qu'un client a besoin de savoir. */
export interface ApiClientOptions {
  /** Racine des appels, sans barre oblique finale. */
  readonly baseUrl: string;
  /** Délai maximal d'une requête, en millisecondes. */
  readonly timeoutMs?: number;
  /** L'implémentation de `fetch`, remplaçable par un test. */
  readonly fetchImplementation?: typeof fetch;
  /** Producteur d'identifiants de corrélation, remplaçable par un test. */
  readonly newCorrelationId?: () => string;
  /**
   * Point d'extension : dernière main sur les en-têtes avant l'envoi.
   *
   * C'est ici que la story de durcissement posera l'authentification. Le
   * squelette ne pose rien, et le dit.
   */
  readonly prepareRequest?: (headers: Headers) => void;
}

/** Le client construit. */
export interface ApiClient {
  /** Envoie une requête et rend la réponse déjà décodée. */
  request<T>(path: string, init?: RequestInit): Promise<T>;
  /**
   * Envoie une requête et rend le corps **binaire**, avec son type.
   *
   * Une image de pièce d'identité ne se lit pas autrement. Un `<img src>`
   * n'emporte **aucun en-tête** — le navigateur émet la requête lui-même, sans
   * jeton porteur —, et le serveur ne pourrait donc ni habiliter la lecture ni
   * l'inscrire au journal d'audit au nom de quelqu'un. Les octets passent donc
   * par le **même** transport que le reste : jeton, identifiant de corrélation
   * et borne de délai compris. L'écran en fait ensuite une URL d'objet.
   */
  requestBytes(path: string, init?: RequestInit): Promise<ApiBytes>;
  /** L'identifiant de corrélation de la dernière requête émise. */
  readonly lastCorrelationId: string | null;
}

/** Un corps binaire, et ce que le serveur a dit qu'il était. */
export interface ApiBytes {
  readonly blob: Blob;
  /** Le `Content-Type` rendu, en minuscules et sans ses paramètres. */
  readonly contentType: string;
}

/**
 * Le délai maximal par défaut.
 *
 * Volontairement plus généreux que sur mobile : un analyste consulte des lots
 * de plusieurs milliers de lignes, et couper à deux secondes lui ferait
 * relancer la même requête trois fois.
 */
export const defaultTimeoutMs = 15_000;

/**
 * Ce qu'un code de statut d'échec dit de l'échec.
 *
 * Trois natures et non deux : voir [ApiErrorKind]. Un 403 n'est ni une panne — le
 * serveur a répondu exactement ce qu'il devait — ni une session finie.
 */
export function natureDuStatut(statut: number): ApiErrorKind {
  if (statut === 401) return 'unauthorized';
  if (statut === 403) return 'forbidden';
  return 'server';
}

/** Construit le client. Aucun état global : une instance par application montée. */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const executer = options.fetchImplementation ?? globalThis.fetch;
  const produireIdentifiant = options.newCorrelationId ?? (() => crypto.randomUUID());
  const delai = options.timeoutMs ?? defaultTimeoutMs;
  let dernierIdentifiant: string | null = null;

  /**
   * L'envoi, commun aux deux lectures.
   *
   * Extrait pour que `requestBytes` hérite **exactement** du même jeton, du
   * même identifiant de corrélation, de la même borne de délai et de la même
   * traduction des statuts. Un second chemin d'envoi écrit à côté aurait fini
   * par diverger sur l'un des quatre — et c'est le jeton qu'on oublie.
   */
  async function envoyer(
    path: string,
    init: RequestInit,
    accept: string,
  ): Promise<{ reponse: Response; correlationId: string }> {
    const correlationId = produireIdentifiant();
    dernierIdentifiant = correlationId;

    const headers = new Headers(init.headers);
    headers.set(correlationHeader, correlationId);
    if (!headers.has('Accept')) headers.set('Accept', accept);
    options.prepareRequest?.(headers);

    const abandon = new AbortController();
    const minuterie = setTimeout(() => abandon.abort(), delai);
    let reponse: Response;
    try {
      reponse = await executer(`${options.baseUrl}${path}`, {
        ...init,
        headers,
        signal: abandon.signal,
      });
    } catch (erreur) {
      // Un abandon est un dépassement de délai, pas une panne réseau : les
      // deux se corrigent différemment, et le message doit les distinguer.
      const depassement = erreur instanceof Error && erreur.name === 'AbortError';
      throw new ApiError({
        kind: depassement ? 'timeout' : 'network',
        status: null,
        correlationId,
      });
    } finally {
      clearTimeout(minuterie);
    }

    if (!reponse.ok) {
      throw new ApiError({
        kind: natureDuStatut(reponse.status),
        status: reponse.status,
        correlationId,
      });
    }
    return { reponse, correlationId };
  }

  return {
    get lastCorrelationId(): string | null {
      return dernierIdentifiant;
    },
    async requestBytes(path: string, init: RequestInit = {}): Promise<ApiBytes> {
      const { reponse } = await envoyer(path, init, '*/*');
      // Le type vient de l'EN-TÊTE, jamais du chemin ni d'une supposition : le
      // serveur l'a reconnu aux octets eux-mêmes, et c'est cette lecture-là qui
      // fait autorité. Les paramètres — `; charset=…` — sont retirés pour que
      // la comparaison côté écran reste une égalité simple.
      const declare = reponse.headers.get('Content-Type') ?? '';
      return {
        blob: await reponse.blob(),
        contentType: declare.split(';')[0].trim().toLowerCase(),
      };
    },
    async request<T>(path: string, init: RequestInit = {}): Promise<T> {
      const { reponse } = await envoyer(path, init, 'application/json');
      // Une réponse sans corps — 204 — n'est pas une erreur de décodage.
      if (reponse.status === 204) return undefined as T;
      return (await reponse.json()) as T;
    },
  };
}
